/**
 * ProfileCompletionBanner — REWIRED.
 *
 * Previously: a full-screen blocking popup that intercepted clicks on every
 * page (including Flow browsing) once a workflow application existed.
 *
 * Now: a small **non-blocking footer chip**. The user can browse the entire
 * platform freely. If there's an in-progress workflow application, the chip
 * gently nudges them to continue. No overlay. No click interception.
 *
 * High-intent gating (Post Lead / Connect / Claim Offer / etc.) is handled
 * separately by `IntentSheet` which is fired on the action itself.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { X, ArrowRight, Plane, GraduationCap } from 'lucide-react';

/** Paths where the chip itself shouldn't render — checkout / completion
 *  pages own the user's attention already, so we don't double-up. */
const HIDDEN_PATHS = [
  '/get-started', '/quick-profile', '/apply', '/complete-profile',
  '/visa-apply', '/admission-apply', '/complete/', '/skill-assessment',
  '/rpl-assessment', '/skill-complete', '/visa-review', '/onboarding',
];

export default function ProfileCompletionBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    // Session-scoped — comes back next session.
    try { return sessionStorage.getItem('completion_chip_dismissed') === '1'; }
    catch { return false; }
  });

  const isHiddenPath = HIDDEN_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (!user || user.role !== 'student') return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/workflow/profile-status`);
        if (!cancelled) setStatus(res.data);
      } catch { /* not logged in or no app */ }
    };
    const onDeleted = () => { setStatus(null); fetchStatus(); };
    window.addEventListener('application-deleted', onDeleted);
    fetchStatus();
    const interval = setInterval(fetchStatus, 180000); // 3 min refresh
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('application-deleted', onDeleted);
    };
  }, [user]);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('completion_chip_dismissed', '1'); } catch { /* ignore */ }
  };

  if (!user || user.role !== 'student') return null;
  if (!status?.has_application || status.completion >= 100) return null;
  if (isHiddenPath || dismissed) return null;

  const isVisa = status.service_type === 'visa' || status.service_type === 'visa_only';
  const pct = status.completion;
  const IconComp = isVisa ? Plane : GraduationCap;
  const ctaText = isVisa ? 'Continue Visa' : 'Continue Application';

  const handleFinish = () => {
    if (status?.next_screen) navigate(status.next_screen);
    else navigate('/complete-profile');
  };

  return (
    <div
      className="fixed bottom-20 right-4 z-40 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-slide-up"
      data-testid="profile-completion-chip"
    >
      <div className="p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1A7A6E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1A7A6E]" /> {pct}% complete
          </span>
          <button
            onClick={dismiss}
            className="w-5 h-5 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400"
            data-testid="completion-chip-close"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2.5">
          <div className="h-1 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: '#1A7A6E' }} />
        </div>
        <div className="flex items-center gap-2 mb-2.5">
          <IconComp className="h-4 w-4 text-slate-400" />
          <p className="text-[12px] font-bold text-[#0F2240] leading-tight">
            {isVisa ? 'Pick up where you left off' : 'Pick up your application'}
          </p>
        </div>
        <button
          onClick={handleFinish}
          className="w-full py-2 rounded-lg bg-[#1A7A6E] hover:bg-[#16685D] text-white font-bold text-[11px] flex items-center justify-center gap-1 active:scale-[0.98] transition-all"
          data-testid="completion-chip-finish"
        >
          {ctaText} <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
